import { useEffect, useState } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { CopyPlus, OctagonX } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import { eventSettingsQueryOptions, patchEvent, type EventSettingsRow } from "@/core/settings"
import { cancelEvent, duplicateEvent } from "@/core/overview"
import { ApiError } from "@/core/api"
import {
  Card,
  Field,
  GhostButton,
  PrimaryButton,
  Select,
  SectionPage,
  SectionPageFooter,
  TextInput,
  Textarea,
} from "@/components/settings/primitives"

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/details")({
  component: DetailsView,
})

// IANA zone list straight from the runtime — no bundled tz table to go stale.
// Older engines without supportedValuesOf fall back to free text.
const ZONES: string[] = (Intl as any).supportedValuesOf?.("timeZone") ?? []

// D1 stores naive wall clocks as YYYY-MM-DDTHH:MM:SS; <input type="datetime-local">
// round-trips YYYY-MM-DDTHH:MM. Normalise both ways so a save can't drop seconds
// off the column format (see project_local_time_only).
const toInput = (v: string | null | undefined) => (v ?? "").slice(0, 16)
const toStored = (v: string) => (v.length === 16 ? `${v}:00` : v)

type FormState = {
  name: string
  shortName: string
  description: string
  startsAt: string
  endsAt: string
  ianaTimezone: string
  venueName: string
  streetAddress: string
  addressLocality: string
  addressRegion: string
  postalCode: string
  addressCountry: string
}

function DetailsView() {
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()
  const q = useQuery({ ...eventSettingsQueryOptions(eventId ?? ""), enabled: !!eventId })

  const [form, setForm] = useState<FormState | null>(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!q.data || dirty) return
    setForm({
      name: q.data.name ?? "",
      shortName: q.data.shortName ?? "",
      description: q.data.description ?? "",
      startsAt: toInput(q.data.startsAt),
      endsAt: toInput(q.data.endsAt),
      ianaTimezone: q.data.ianaTimezone ?? "",
      venueName: q.data.venueName ?? "",
      streetAddress: q.data.streetAddress ?? "",
      addressLocality: q.data.addressLocality ?? "",
      addressRegion: q.data.addressRegion ?? "",
      postalCode: q.data.postalCode ?? "",
      addressCountry: q.data.addressCountry ?? "",
    })
  }, [q.data, dirty])

  const update = (patch: Partial<FormState>) => {
    setDirty(true)
    setForm((f) => (f ? { ...f, ...patch } : f))
  }

  const save = useMutation({
    mutationFn: () => {
      if (!eventId || !form) throw new Error("Not ready")
      if (!form.name.trim()) throw new ApiError("Name is required", 400)
      if (!form.startsAt || !form.endsAt) throw new ApiError("Start and end are required", 400)
      if (form.endsAt < form.startsAt) throw new ApiError("End must be after start", 400)
      const patch: Partial<EventSettingsRow> = {
        name: form.name.trim(),
        shortName: form.shortName.trim() || null,
        description: form.description.trim() || null,
        startsAt: toStored(form.startsAt),
        endsAt: toStored(form.endsAt),
        ianaTimezone: form.ianaTimezone.trim() || null,
        venueName: form.venueName.trim() || null,
        streetAddress: form.streetAddress.trim() || null,
        addressLocality: form.addressLocality.trim() || null,
        addressRegion: form.addressRegion.trim() || null,
        postalCode: form.postalCode.trim() || null,
        addressCountry: form.addressCountry.trim().toUpperCase() || null,
      }
      return patchEvent(eventId, patch)
    },
    onSuccess: () => {
      setDirty(false)
      toast.success("Event details saved")
      void qc.invalidateQueries({ queryKey: ["resource", "events", eventId] })
      // The switcher / header read the name from their own lists.
      void qc.invalidateQueries({ queryKey: ["org-events"] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to save event details"),
  })

  const disabled = !form

  return (
    <SectionPage
      title="Event details"
      description="The name, dates and venue every guest surface reads from."
    >
      {q.isError && (
        <Card>
          <p className="text-[13px] text-rose-600 dark:text-rose-400">
            {(q.error as Error)?.message ?? "Failed to load event details"}
          </p>
        </Card>
      )}

      <Card title="Basics">
        <Field label="Name" hint="The canonical title — hero cards, invites, calendar entries.">
          <TextInput
            value={form?.name ?? ""}
            disabled={disabled}
            maxLength={200}
            placeholder="Acme Summit 2027"
            onChange={(e) => update({ name: e.target.value })}
          />
        </Field>
        <Field label="Short name" hint="Optional compact label for headers, switcher rows and push titles.">
          <TextInput
            value={form?.shortName ?? ""}
            disabled={disabled}
            maxLength={60}
            placeholder="Acme Summit"
            onChange={(e) => update({ shortName: e.target.value })}
          />
        </Field>
        <Field label="URL slug" hint="Permanent — event links and QR codes depend on it.">
          <TextInput value={q.data?.slug ?? ""} disabled readOnly className="font-mono" />
        </Field>
        <Field label="Description">
          <Textarea
            value={form?.description ?? ""}
            disabled={disabled}
            maxLength={5000}
            rows={4}
            placeholder="What this event is, for the invite and event site."
            onChange={(e) => update({ description: e.target.value })}
          />
        </Field>
      </Card>

      <Card title="Dates" description="Local time at the venue — no offset is applied.">
        <div className="grid gap-4 @[560px]:grid-cols-2">
          <Field label="Starts">
            <TextInput
              type="datetime-local"
              value={form?.startsAt ?? ""}
              disabled={disabled}
              onChange={(e) => update({ startsAt: e.target.value })}
            />
          </Field>
          <Field label="Ends">
            <TextInput
              type="datetime-local"
              value={form?.endsAt ?? ""}
              disabled={disabled}
              onChange={(e) => update({ endsAt: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Timezone" hint="IANA zone at the venue. Used to schedule reminders at the right moment.">
          {ZONES.length > 0 ? (
            <Select
              value={form?.ianaTimezone ?? ""}
              disabled={disabled}
              onChange={(e) => update({ ianaTimezone: e.target.value })}
            >
              <option value="">Not set</option>
              {form?.ianaTimezone && !ZONES.includes(form.ianaTimezone) && (
                <option value={form.ianaTimezone}>{form.ianaTimezone}</option>
              )}
              {ZONES.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </Select>
          ) : (
            <TextInput
              value={form?.ianaTimezone ?? ""}
              disabled={disabled}
              placeholder="America/New_York"
              onChange={(e) => update({ ianaTimezone: e.target.value })}
            />
          )}
        </Field>
      </Card>

      <Card title="Location">
        <Field label="Venue">
          <TextInput
            value={form?.venueName ?? ""}
            disabled={disabled}
            maxLength={200}
            placeholder="The Grand Hotel"
            onChange={(e) => update({ venueName: e.target.value })}
          />
        </Field>
        <Field label="Street address">
          <TextInput
            value={form?.streetAddress ?? ""}
            disabled={disabled}
            maxLength={300}
            placeholder="1 Grand Avenue"
            onChange={(e) => update({ streetAddress: e.target.value })}
          />
        </Field>
        <div className="grid gap-4 @[560px]:grid-cols-2">
          <Field label="City">
            <TextInput
              value={form?.addressLocality ?? ""}
              disabled={disabled}
              maxLength={100}
              onChange={(e) => update({ addressLocality: e.target.value })}
            />
          </Field>
          <Field label="State / region">
            <TextInput
              value={form?.addressRegion ?? ""}
              disabled={disabled}
              maxLength={100}
              onChange={(e) => update({ addressRegion: e.target.value })}
            />
          </Field>
          <Field label="Postal code">
            <TextInput
              value={form?.postalCode ?? ""}
              disabled={disabled}
              maxLength={20}
              onChange={(e) => update({ postalCode: e.target.value })}
            />
          </Field>
          <Field label="Country" hint="Two-letter ISO code, e.g. US.">
            <TextInput
              value={form?.addressCountry ?? ""}
              disabled={disabled}
              maxLength={2}
              placeholder="US"
              className="font-mono uppercase"
              onChange={(e) => update({ addressCountry: e.target.value })}
            />
          </Field>
        </div>
      </Card>

      <LifecycleCard eventId={eventId ?? null} eventName={q.data?.name ?? ""} />

      <SectionPageFooter>
        <PrimaryButton onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : "Save changes"}
        </PrimaryButton>
      </SectionPageFooter>
    </SectionPage>
  )
}

// ── lifecycle ─────────────────────────────────────────────────────────────────
// Duplicate (new draft, new slug) and Cancel (terminal — rescinds outstanding
// guests; owner/admin only, the backend enforces it).

function LifecycleCard({ eventId, eventName }: { eventId: string | null; eventName: string }) {
  const { orgSlug } = useActiveEvent()
  const qc = useQueryClient()
  const navigate = useNavigate()

  const duplicate = useMutation({
    mutationFn: () => duplicateEvent(eventId!),
    onSuccess: (r) => {
      toast.success("Event duplicated — you're now on the draft copy")
      void qc.invalidateQueries({ queryKey: ["org-events"] })
      if (orgSlug && r.slug)
        void navigate({ to: "/$orgSlug/$eventSlug", params: { orgSlug, eventSlug: r.slug } })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to duplicate"),
  })

  const cancel = useMutation({
    mutationFn: () => cancelEvent(eventId!),
    onSuccess: () => {
      toast.success("Event cancelled")
      void qc.invalidateQueries({ queryKey: ["resource", "events", eventId] })
      void qc.invalidateQueries({ queryKey: ["org-events"] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to cancel event"),
  })

  return (
    <Card
      title="Duplicate or cancel"
      description="Clone this event as a fresh draft, or shut it down."
    >
      <div className="flex flex-wrap items-center gap-2">
        <GhostButton
          onClick={() => duplicate.mutate()}
          disabled={!eventId || duplicate.isPending}
        >
          <CopyPlus className="h-4 w-4" />
          {duplicate.isPending ? "Duplicating…" : "Duplicate event"}
        </GhostButton>
        <GhostButton
          className="text-rose-600 dark:text-rose-400"
          disabled={!eventId || cancel.isPending}
          onClick={() => {
            if (
              confirm(
                `Cancel "${eventName}"? All outstanding guests are rescinded and this can't be undone.`,
              )
            )
              cancel.mutate()
          }}
        >
          <OctagonX className="h-4 w-4" />
          {cancel.isPending ? "Cancelling…" : "Cancel event"}
        </GhostButton>
      </div>
    </Card>
  )
}
